import React from 'react'
import { cn } from './cn'

/**
 * 紅黑分工見 design-guideline.md §2.4：
 * - `brand`（紅實心）＝**這個畫面的主要動作**。硬規則：一個 viewport／
 *   當前操作層級最多一顆。對話框開啟時焦點讓給對話框內那一顆。
 * - `primary`（黑實心）＝常駐 chrome 的跨頁入口與次級動作。
 * - `danger`（深紅實心）＝破壞性動作，**只在觸發刪除後**出現於確認介面，
 *   不常駐在列上；未觸發時刪除入口用 IconButton 的墨黑圖示。
 *
 * 墨底區塊（隱私帶、文末 CTA、下載帶）用 `onlight`／`ondark` 這一對：紙白實心是
 * 那一塊的主要動作，描邊是次要。它們不吃「一個 viewport 一顆紅」的預算，因為
 * 墨底上的紙白不是紅——但同一塊墨底上仍然只放一顆 `onlight`。
 */
export type ButtonVariant =
  | 'brand'
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'ghost-inverse'
  | 'inverse'
  | 'onlight'
  | 'ondark'
  | 'danger'
  | 'text'
  | 'text-danger'
export type ButtonSize = 'md' | 'sm' | 'compact' | 'control' | 'marketing' | 'fmt' | 'row'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  /** fmt = 等寬小型按鈕（VTT/SRT 等格式鈕） */
  size?: ButtonSize
}

const BASE =
  'inline-flex cursor-pointer whitespace-nowrap items-center gap-2 border border-transparent rounded-[var(--radius)] px-[var(--inset-button-inline)] py-[var(--inset-button-block)] text-base leading-[var(--leading-body)] font-medium transition-[transform_0.05s_ease,background_0.15s_ease,border-color_0.15s_ease,opacity_0.15s_ease,text-decoration-color_0.15s_ease] disabled:cursor-not-allowed disabled:opacity-40'

const INTERACTION =
  'active:translate-y-px focus-visible:outline-2 focus-visible:outline-[var(--focus-ring)] focus-visible:outline-offset-2'

const VARIANT: Record<ButtonVariant, string> = {
  brand: 'border-brand bg-brand text-background enabled:hover:bg-brand-hover',
  danger:
    'border-danger bg-danger text-background enabled:hover:bg-[color-mix(in_oklch,var(--danger)_88%,var(--fg))]',
  primary:
    'border-foreground bg-foreground text-background enabled:hover:bg-[color-mix(in_oklch,var(--fg)_80%,var(--bg))]',
  secondary: 'border-border bg-background text-foreground enabled:hover:border-foreground',
  ghost: 'border-transparent bg-transparent px-2 text-muted enabled:hover:text-foreground',
  'ghost-inverse':
    'border-transparent bg-transparent px-2 text-inherit enabled:hover:text-inherit enabled:hover:opacity-65',
  inverse:
    'border-current bg-transparent text-inherit enabled:hover:border-current enabled:hover:bg-transparent',
  onlight:
    'border-background bg-background text-foreground focus-visible:outline-[var(--bg)] enabled:hover:bg-[color-mix(in_oklch,var(--bg)_86%,var(--fg))]',
  ondark:
    'border-background/38 bg-transparent text-background focus-visible:outline-[var(--bg)] enabled:hover:border-background enabled:hover:bg-background/10',
  // design-guideline.md §7.5（Open Design 主文件）：文字按鈕沒有色塊可以動，唯一的槓桿是
  // 底線——常駐一階較淡，hover 只加深底線到 --fg，文字色與底色都不變（改文字色會直接
  // 降低對比，違反同一條規則）。
  text: 'border-transparent bg-transparent !px-1 text-foreground underline decoration-[var(--line-strong)] underline-offset-4 enabled:hover:decoration-[var(--fg)]',
  'text-danger':
    'border-transparent bg-transparent !px-1 text-danger underline decoration-[var(--danger-line)] underline-offset-4 enabled:hover:decoration-[var(--danger)]',
}

/** Links do not match `:enabled`; retain the same visual hover contract. */
const LINK_VARIANT: Record<ButtonVariant, string> = {
  brand: 'border-brand bg-brand text-background hover:bg-brand-hover',
  danger:
    'border-danger bg-danger text-background hover:bg-[color-mix(in_oklch,var(--danger)_88%,var(--fg))]',
  primary:
    'border-foreground bg-foreground text-background hover:bg-[color-mix(in_oklch,var(--fg)_80%,var(--bg))]',
  secondary: 'border-border bg-background text-foreground hover:border-foreground',
  ghost: 'border-transparent bg-transparent px-2 text-muted hover:text-foreground',
  'ghost-inverse':
    'border-transparent bg-transparent px-2 text-inherit hover:text-inherit hover:opacity-65',
  inverse: 'border-current bg-transparent text-inherit hover:border-current hover:bg-transparent',
  onlight:
    'border-background bg-background text-foreground focus-visible:outline-[var(--bg)] hover:bg-[color-mix(in_oklch,var(--bg)_86%,var(--fg))]',
  ondark:
    'border-background/38 bg-transparent text-background focus-visible:outline-[var(--bg)] hover:border-background hover:bg-background/10',
  text: 'border-transparent bg-transparent !px-1 text-foreground underline decoration-[var(--line-strong)] underline-offset-4 hover:decoration-[var(--fg)]',
  'text-danger':
    'border-transparent bg-transparent !px-1 text-danger underline decoration-[var(--danger-line)] underline-offset-4 hover:decoration-[var(--danger)]',
}

/** A disabled link still has no native `:disabled` selector. Keep its surface static. */
const UNAVAILABLE_LINK_VARIANT: Record<ButtonVariant, string> = {
  brand: 'border-brand bg-brand text-background',
  danger: 'border-danger bg-danger text-background',
  primary: 'border-foreground bg-foreground text-background',
  secondary: 'border-border bg-background text-foreground',
  ghost: 'border-transparent bg-transparent px-2 text-muted',
  'ghost-inverse': 'border-transparent bg-transparent px-2 text-inherit',
  inverse: 'border-current bg-transparent text-inherit',
  onlight: 'border-background bg-background text-foreground',
  ondark: 'border-background/38 bg-transparent text-background',
  text: 'border-transparent bg-transparent !px-1 text-foreground underline decoration-[var(--line-strong)] underline-offset-4',
  'text-danger':
    'border-transparent bg-transparent !px-1 text-danger underline decoration-[var(--danger-line)] underline-offset-4',
}

const SIZE: Record<ButtonSize, string> = {
  md: '',
  sm: 'px-3 py-1.5 text-sm leading-[var(--leading-body)]',
  compact:
    'px-[var(--inset-button-compact-inline)] py-[var(--inset-button-compact-block)] text-sm leading-[var(--leading-body)]',
  // Inline form actions share the 38px control geometry with Input / Select.
  // `!py-0` deliberately wins over BASE so a caller cannot end up with a
  // fixed height plus vertical padding at the same time.
  control: 'h-[var(--ctl-h)] !py-0',
  marketing:
    'px-[var(--inset-button-marketing-inline)] py-[var(--inset-button-marketing-block)] text-md leading-[var(--leading-body)]',
  fmt: 'px-2.5 py-[5px] font-mono text-xs tracking-[0.04em]',
  // App 面的滑鼠尺寸：高度跟清單列、圖示鈕同一個 --d-row，字重 600（設計側
  // css/kiao-desktop-sleek.css 的 .mac .btn）。文字按鈕在下面 sizeClassFor 另外處理。
  row: 'h-[var(--d-row)] !py-0 px-3.5 text-sm leading-[var(--leading-body)] font-semibold [&_svg]:size-4',
}

const TEXT_VARIANTS: ReadonlySet<ButtonVariant> = new Set<ButtonVariant>(['text', 'text-danger'])

function sizeClassFor(variant: ButtonVariant, size: ButtonSize): string {
  // Ghost marketing links retain the intentionally compact 8px horizontal
  // inset from the landing prototype while adopting the larger 15px type and
  // 12px block inset. Other ghost sizes keep their historical size map.
  if ((variant === 'ghost' || variant === 'ghost-inverse') && size === 'marketing') {
    return 'py-[var(--inset-button-marketing-block)] text-md leading-[var(--leading-body)]'
  }
  // A text button carries no fill, so the row size's heavier weight would make
  // it read as a second solid action beside the real one (.btn.txt is 500).
  if (size === 'row' && TEXT_VARIANTS.has(variant)) return `${SIZE.row} font-medium`
  return SIZE[size]
}

export function buttonClassName(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  extra?: string
): string {
  return cn(BASE, INTERACTION, VARIANT[variant], sizeClassFor(variant, size), extra)
}

/** Shared button visual for anchor/router-link shapes, whose hover state is not `:enabled`. */
export function buttonLinkClassName(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  extra?: string
): string {
  return cn(BASE, INTERACTION, LINK_VARIANT[variant], sizeClassFor(variant, size), extra)
}

function unavailableButtonLinkClassName(
  variant: ButtonVariant,
  size: ButtonSize,
  extra?: string
): string {
  return cn(BASE, UNAVAILABLE_LINK_VARIANT[variant], sizeClassFor(variant, size), extra)
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className, type, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      {...rest}
      data-ui="button"
      data-button-variant={variant}
      className={buttonClassName(variant, size, className)}
    />
  )
})

export interface ButtonLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Pass a router link component while retaining the shared button treatment. */
  as?: React.ElementType
  /** A visible, intentionally non-interactive control (for example, a missing download). */
  unavailable?: boolean
  /** A stable state hook for consumers that need to distinguish unavailable placeholders. */
  'data-button-state'?: string
}

/**
 * 連結形態的按鈕：行銷頁 CTA 多為錨點/外連的 `<a>`，需要按鈕外觀。
 * `as={Link}` 可保留 Next 客戶端路由，同時不犧牲共用按鈕的互動與外觀。
 */
export function ButtonLink({
  variant = 'primary',
  size = 'md',
  className,
  as: Component = 'a',
  unavailable = false,
  'aria-disabled': ariaDisabled,
  'data-button-state': buttonState,
  onClick,
  tabIndex,
  ...rest
}: ButtonLinkProps) {
  const ariaUnavailable = ariaDisabled === true || ariaDisabled === 'true'
  const isUnavailable = unavailable || ariaUnavailable
  const onUnavailableClick: React.MouseEventHandler<HTMLAnchorElement> = (event) =>
    event.preventDefault()
  return (
    <Component
      {...rest}
      aria-disabled={isUnavailable || undefined}
      onClick={isUnavailable ? onUnavailableClick : onClick}
      tabIndex={isUnavailable ? -1 : tabIndex}
      data-ui="button"
      data-button-variant={variant}
      data-button-state={unavailable ? 'unavailable' : buttonState}
      className={
        unavailable
          ? unavailableButtonLinkClassName(
              variant,
              size,
              cn(className, '!cursor-not-allowed opacity-40')
            )
          : buttonLinkClassName(
              variant,
              size,
              cn(className, ariaUnavailable && '!cursor-default pointer-events-none')
            )
      }
    />
  )
}
